/**
 * The unified LLM usage report's wire types — the TypeScript mirror of
 * goapi/pkg/types/llmusage.go.
 *
 * THE ONE RULE ENCODED HERE: every optional field below is optional because the
 * SERVER decides whether to send it, not because the UI sometimes ignores it.
 *
 *   - `cost` / `currency` are absent on customer-facing mounts. The job-scoped
 *     endpoint never emits them, so the component renders a cost column only
 *     when the payload has one. Do not default them to 0 — a zero in a cost
 *     column reads as "this was free", which is a different claim from "you are
 *     not shown money here".
 *   - the queue block (`lane`, `state`, `request_id`, timings) is absent for a
 *     call that was never queued. Only AutoCoder coding and ThemeAI go through
 *     the queue; table analysis, the agent and generic chat do not. An absent
 *     lane means "never queued", never "idle".
 */

export interface LlmUsageRow {
  id: string
  created_at: number
  user_id: string
  customer: string
  job?: string
  feature_key: string
  model: string
  input_tokens: number
  output_tokens: number
  total_tokens: number
  cached_tokens: number
  cache_creation_tokens: number
  reasoning_tokens: number

  /** The parent this call belongs to: a coding batch id, a ThemeAI run id. */
  client_ref?: string

  /** Admin mounts only. Absent means "not shown", not "free". */
  cost?: number
  currency?: string

  /** Present only when the call was queued. */
  request_id?: string
  request_type?: string
  lane?: string
  state?: string
  estimated_tokens?: number
  attempt_count?: number
  request_error?: string
  queued_at?: string
  started_at?: string
  completed_at?: string
}

export interface LlmUsageListResult {
  rows: LlmUsageRow[]
  total_count: number
  limit: number
  offset: number
}

export interface LlmUsageDimension {
  dimension: string
  request_count: number
  total_tokens: number
  cost?: number
}

export interface LlmUsageSummary {
  request_count: number
  total_input_tokens: number
  total_output_tokens: number
  total_tokens: number
  total_cached_tokens: number
  total_cache_creation_tokens: number
  total_reasoning_tokens: number
  total_cost?: number
  currency?: string
  by_feature: LlmUsageDimension[]
  by_model: LlmUsageDimension[]
  /**
   * One row per value of the grouped label key, plus an UNATTRIBUTED bucket
   * (`dimension: ''`) for rows carrying no such key — pre-label-era spend and
   * any batch nobody grouped. Real money, shown rather than dropped.
   *
   * Present only when the query asked for `group_by: 'label:<key>'`, and only
   * ever on the ADMIN mount: the job-scoped handler strips a label grouping
   * before it reaches the store, because enumerating campaign ids to the
   * customer they describe is disclosure.
   */
  by_label?: LlmUsageDimension[]
  /**
   * Present only when the query asked for `group_by: 'user_id'`: one row per
   * billed user, biggest first, plus an empty-string bucket for calls billed
   * to nobody (background helpers with no user in scope). Admin mounts only.
   */
  by_user?: LlmUsageDimension[]
  /**
   * Present only when the query asked for `group_by: 'team_id'`: one row per
   * budget team id (stamped on the usage row at write, #1220), plus an
   * empty-string bucket for calls charged to no team.
   */
  by_team?: LlmUsageDimension[]
}

/** Every way the report can be narrowed. Mounts lock some of these. */
export interface LlmUsageFilters {
  customer?: string
  job?: string
  user_id?: string
  feature?: string
  features?: string[]
  model?: string
  request_type?: string
  lane?: string
  state?: string
  client_ref?: string
  /** A budget team id (#1220). */
  team_id?: string
  /**
   * The one extra breakdown the summary computes: `label:<key>`, `model`, or
   * `feature_key`. Admin mounts only — see `by_label`.
   */
  group_by?: string
  start_time?: number
  end_time?: number
  limit?: number
  offset?: number
}

/**
 * Which mount this is. The scope decides the URL and therefore, server-side,
 * whether money comes back at all — the component never makes that decision.
 */
export type LlmUsageScope =
  | { kind: 'admin' }
  | { kind: 'job'; customer: string; job: string }
  | { kind: 'parent'; customer: string; job: string; clientRef: string }

/** The time windows the picker offers. `null` means all time. */
export const USAGE_WINDOWS: { key: string; label: string; hours: number | null }[] = [
  { key: '24h', label: 'Last 24 hours', hours: 24 },
  { key: '7d', label: 'Last 7 days', hours: 24 * 7 },
  { key: '30d', label: 'Last 30 days', hours: 24 * 30 },
  { key: 'all', label: 'All time', hours: null },
]
